/**
 * CANONICAL FRONTEND TRANSPORT CONTRACT
 *
 * ALL authenticated HTTP requests MUST use one of the axios clients exported
 * from this module (`apiClient`, `simulationApiClient`, or a custom instance
 * that reuses `attachAuthToken`).  Raw `fetch()` with manual Authorization
 * headers is NOT allowed in production code, with these exceptions:
 *
 *   1. **AuthContext bootstrap** — login, register, token verify/refresh need
 *      raw fetch because they *create* the tokens that the interceptor reads.
 *
 *   2. **Public / unauthenticated endpoints** — e.g. health checks, public
 *      pricing tiers, GeoJSON fetches.  These carry no Authorization header
 *      and therefore do not need the interceptor.
 *
 * Choosing a client:
 *   - `apiClient`            — standard requests to FastAPI (30 s timeout)
 *   - `simulationApiClient`  — long-running simulation calls (5 min timeout)
 *   - `authApiClient`        — requests to Django auth service (AUTH_BASE_URL,
 *     30 s timeout).  Uses `attachAuthToken` + `handle401` interceptors,
 *     identical to apiClient.  Import from this module.
 *   - `djangoApiClient`      — requests to non-auth Django APIs
 *     (DJANGO_API_BASE_URL, falling back to AUTH_BASE_URL) with the same auth
 *     behavior.
 *
 * Auth header injection:
 *   `attachAuthToken` reads the JWT from `localStorage('auth_tokens').access`
 *   and sets `Authorization: Bearer <token>`.  When no token is present the
 *   request proceeds without the header.
 *
 * 401 handling:
 *   Both `apiClient` and `simulationApiClient` include an automatic token
 *   refresh / replay interceptor:
 *
 *   1. On a 401 response the interceptor attempts to refresh the access token
 *      by calling `AUTH_BASE_URL/api/users/auth/token/refresh/` with the
 *      stored refresh token.
 *   2. Concurrent requests that arrive while a refresh is in flight are
 *      queued and replayed once the new access token is available.
 *   3. If the refresh call itself fails (e.g. expired refresh token) all
 *      queued requests are rejected, stored tokens are cleared, and the
 *      browser is redirected to `/` so the user can re-authenticate.
 *
 *   The refresh endpoint is the Django SimpleJWT endpoint at
 *   `AUTH_BASE_URL/api/users/auth/token/refresh/` (same URL used by
 *   AuthContext for the initial refresh on boot).
 *
 * Migration status (2026-04):
 *   - subscription.ts        — DONE  (uses shared authApiClient from this module)
 *   - userForests.ts         — DONE  (uses apiClient + handle401 helper)
 *   - PropertySubscriptionContext — DONE (uses shared authApiClient from this module)
 *   - AuthContext.tsx         — EXEMPT (auth bootstrap, must use raw fetch)
 */

import axios from "axios";
import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { resolveBrowserServiceEndpoints } from "./baseUrl";
import { logResponseFailure, logTransportError } from "./transportLog";

declare module "axios" {
  interface AxiosRequestConfig {
    skipErrorLogging?: boolean | ((error: unknown) => boolean);
    /**
     * When true, a 401 response does NOT trigger the global handle401 interceptor
     * (token refresh + clearTokensAndRedirect).  Use for optional/non-critical
     * requests whose callers already have catch blocks for graceful fallback — e.g.
     * user preference fetches that fall back to defaults on failure.
     *
     * Without this flag, a 401 on a non-critical call can cascade into a full
     * logout even when the user's session is still valid.
     */
    skipAuth401?: boolean; omitAuthorization?: boolean;
  }

  interface InternalAxiosRequestConfig {
    skipErrorLogging?: boolean | ((error: unknown) => boolean);
    skipAuth401?: boolean; omitAuthorization?: boolean;
  }
}

const viteEnv = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
const isViteDev = Boolean(viteEnv?.DEV);
const viteApiBaseUrl =
  typeof viteEnv?.VITE_API_BASE_URL === "string" ? viteEnv.VITE_API_BASE_URL : undefined;
const viteAuthBaseUrl =
  typeof viteEnv?.VITE_AUTH_BASE_URL === "string" ? viteEnv.VITE_AUTH_BASE_URL : undefined;
const viteDjangoApiBaseUrl =
  typeof viteEnv?.VITE_DJANGO_API_BASE_URL === "string"
    ? viteEnv.VITE_DJANGO_API_BASE_URL
    : undefined;

const {
  apiBaseUrl: API_BASE_URL,
  authBaseUrl: AUTH_BASE_URL,
  djangoApiBaseUrl: DJANGO_API_BASE_URL,
} = resolveBrowserServiceEndpoints({
  isDevelopment: isViteDev,
  apiBaseUrl: viteApiBaseUrl,
  authBaseUrl: viteAuthBaseUrl,
  djangoApiBaseUrl: viteDjangoApiBaseUrl,
});

// Token refresh / replay state

/**
 * Whether a token refresh request is currently in-flight.
 * Exported so unit tests can inspect and reset this state.
 */
export let isRefreshing = false;

/**
 * Queue of { resolve, reject } callbacks for requests that arrived while a
 * refresh was already in progress.  Each entry corresponds to one pending
 * axios request.  Once the refresh completes (or fails) the queue is drained.
 */
export const failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

/** Drain the failedQueue — resolve with the new token or reject with an error. */
export function processQueue(error: unknown, token: string | null): void {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(token!);
    }
  });
  failedQueue.length = 0;
}

/**
 * Perform the token refresh call against the Django SimpleJWT endpoint.
 * Updates `localStorage('auth_tokens')` with the new pair and returns the
 * new access token.
 *
 * Throws if the refresh token is missing or the network call fails.
 *
 * @internal  Exported for unit-test injection via `__setRefreshImpl`.
 */
export async function _defaultRefreshImpl(): Promise<string> {
  const raw = localStorage.getItem("auth_tokens");
  if (!raw) {
    throw new Error("No auth_tokens in localStorage — cannot refresh");
  }

  const { refresh } = JSON.parse(raw) as { refresh?: string };
  if (!refresh) {
    throw new Error("No refresh token stored — cannot refresh");
  }

  const response = await fetch(`${AUTH_BASE_URL}/api/users/auth/token/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const data = (await response.json()) as { access: string; refresh?: string };
  const newTokens = {
    access: data.access,
    refresh: data.refresh ?? refresh, // SimpleJWT may rotate the refresh token
  };
  localStorage.setItem("auth_tokens", JSON.stringify(newTokens));
  return newTokens.access;
}

/**
 * Replaceable refresh implementation — allows unit tests to inject a stub
 * without patching `fetch` globally.
 */
let _refreshImpl: () => Promise<string> = _defaultRefreshImpl;

/** @internal Replace the refresh implementation (unit tests only). */
export function __setRefreshImpl(impl: () => Promise<string>): void {
  _refreshImpl = impl;
}

/** @internal Reset all token-refresh state (unit tests only). */
export function __resetRefreshState(): void {
  isRefreshing = false;
  failedQueue.length = 0;
  _refreshImpl = _defaultRefreshImpl;
  _authFailNavigate = null;
  _authFailShowError = null;
}

// Auth-fail observability — trace ID, structured logging, router navigation

/** Generate a UUID v4 for auth-fail correlation. */
function generateTraceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // UUID v4 polyfill for environments without crypto.randomUUID
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** React-router navigate fn, registered by AuthFailCoordinator on mount. */
let _authFailNavigate: ((path: string, opts?: { replace?: boolean }) => void) | null = null;

/** User-visible auth-fail feedback fn, registered by AuthFailCoordinator on mount. */
let _authFailShowError: ((msg: string) => void) | null = null;

/** Register (or clear) the react-router navigate function. Pass null to deregister on unmount. */
export function setAuthFailNavigate(
  fn: ((path: string, opts?: { replace?: boolean }) => void) | null,
): void {
  _authFailNavigate = fn;
}

/** Register (or clear) the auth-fail feedback function. Pass null to deregister on unmount. */
export function setAuthFailShowError(fn: ((msg: string) => void) | null): void {
  _authFailShowError = fn;
}

/** @internal Reset auth-fail observability state (unit tests only). */
export function __resetAuthFailObservability(): void {
  _authFailNavigate = null;
  _authFailShowError = null;
  _creditExhaustedHandler = null;
}

// Credit exhaustion (HTTP 402 insufficient_credits) — global handler

/**
 * Parsed payload of an `insufficient_credits` 402 response. Mirrors the Django
 * envelope built in backend_django/users/credit_views.py and passed through the
 * gateway (backend/app/services/credit_action_gate.py).
 */
export interface InsufficientCreditsDetail {
  actionKey: string;
  /** Exact property from the rejected decision-action request, when supplied. */
  propertyId?: string;
  /** Cost of the attempted action in credits, when the backend reports it. */
  creditCost?: number;
  /** Current balance in credits, when the backend reports it. */
  balance?: number;
  /** Human-readable backend detail string. */
  detail: string;
}

/** Registered by CreditTopUpProvider to open the universal top-up popup. */
let _creditExhaustedHandler: ((detail: InsufficientCreditsDetail) => void) | null = null;

/** Register (or clear) the credit-exhausted handler. Pass null to deregister. */
export function setCreditExhaustedHandler(
  fn: ((detail: InsufficientCreditsDetail) => void) | null,
): void {
  _creditExhaustedHandler = fn;
}

/**
 * If `error` is a 402 with `error_code: "insufficient_credits"`, parse its
 * envelope and fire the registered handler (opening the universal top-up popup).
 *
 * This does NOT swallow the error — callers' own catch blocks still run so they
 * can stop spinners / clear local state. It only surfaces the global popup.
 */
/** A finite number, else undefined — never coerces missing credit data to 0. */
function finiteNumberOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/**
 * Resolve the insufficient-credits envelope, which arrives either at the top
 * level (Django direct) or nested under `detail` (the FastAPI gateway re-raises
 * HTTPException(detail=body)). The compute-quota 402 has a string `detail` and
 * no error_code, so it correctly does NOT match.
 */
function resolveInsufficientCreditsEnvelope(
  data: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!data) return undefined;
  if (data.error_code === "insufficient_credits") return data;

  const nested = data.detail;
  if (
    typeof nested === "object" &&
    nested !== null &&
    (nested as Record<string, unknown>).error_code === "insufficient_credits"
  ) {
    return nested as Record<string, unknown>;
  }
  return undefined;
}

function propertyIdFromRequestData(data: unknown): string | undefined {
  let body: unknown = data;
  if (typeof data === "string") {
    try {
      body = JSON.parse(data);
    } catch {
      return undefined;
    }
  }
  if (typeof body !== "object" || body === null) return undefined;
  const propertyId = (body as Record<string, unknown>).property_id;
  return typeof propertyId === "string" && propertyId.length > 0
    ? propertyId
    : undefined;
}

export function maybeHandleInsufficientCredits(error: unknown): void {
  if (!_creditExhaustedHandler) return;
  const response = (error as ErrorWithResponseStatus | undefined)?.response;
  if (response?.status !== 402) return;

  const envelope = resolveInsufficientCreditsEnvelope(
    response.data as Record<string, unknown> | undefined,
  );
  if (!envelope) return;

  _creditExhaustedHandler({
    actionKey: typeof envelope.action_key === "string" ? envelope.action_key : "",
    propertyId: propertyIdFromRequestData(
      (error as ErrorWithResponseStatus | undefined)?.config?.data,
    ),
    creditCost: finiteNumberOrUndefined(envelope.credit_cost),
    balance: finiteNumberOrUndefined(envelope.balance),
    detail: typeof envelope.detail === "string" ? envelope.detail : "",
  });
}

// Shared 401 response error handler — used by both axios clients

/**
 * Handle a 401 response on `client`.  Queues the original request if a
 * refresh is already in-flight; otherwise starts a new refresh, replays
 * the original request with the new token, and drains the queue.
 *
 * On refresh failure: logs a structured [auth-fail] event, surfaces a
 * user-visible error, clears localStorage tokens, and navigates to `/`.
 */
export async function handle401(
  client: ReturnType<typeof axios.create>,
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean; _authTraceId?: string },
): Promise<AxiosResponse> {
  const traceId = originalRequest._authTraceId ?? generateTraceId();
  const triggerStr = `${String(originalRequest.method ?? "UNKNOWN").toUpperCase()} ${String(originalRequest.url ?? "unknown")} 401`;

  // Prevent infinite loops: if this request already carried a refreshed
  // token and still got a 401, it is a genuine auth failure.
  if (originalRequest._retry) {
    clearTokensAndRedirect({
      trigger: triggerStr,
      refresh_outcome: "ok (refresh succeeded, retry still 401)",
      trace_id: traceId,
    });
    return Promise.reject(
      new AuthSessionExpiredError("Token refresh did not resolve 401"),
    );
  }

  if (isRefreshing) {
    // Another refresh is in-flight — queue this request.
    return new Promise<AxiosResponse>((resolve, reject) => {
      failedQueue.push({
        resolve: (token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(client(originalRequest));
        },
        reject,
      });
    });
  }

  // Start a fresh refresh.
  originalRequest._retry = true;
  originalRequest._authTraceId = traceId;
  isRefreshing = true;

  try {
    const newToken = await _refreshImpl();
    isRefreshing = false;
    processQueue(null, newToken);
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    // Tag the retry with the trace id so backend access logs can correlate.
    (originalRequest.headers as Record<string, string>)["X-Auth-Fail-Trace"] = traceId;
    return client(originalRequest);
  } catch (refreshError) {
    const outcome =
      refreshError instanceof Error ? refreshError.message : String(refreshError);
    // Same terminal 401 for this request and for every request queued behind the
    // refresh: the session is gone for all of them, and none should be retried.
    const expired = new AuthSessionExpiredError(
      `Session expired (${triggerStr}): ${outcome}`,
      refreshError,
    );
    isRefreshing = false;
    processQueue(expired, null);
    clearTokensAndRedirect({
      trigger: triggerStr,
      refresh_outcome: outcome,
      trace_id: traceId,
      stack: refreshError instanceof Error ? refreshError.stack : undefined,
    });
    return Promise.reject(expired);
  }
}

/**
 * The 401 the caller actually received, after `handle401` exhausted its refresh
 * and replay options.
 *
 * `handle401` consumes the original axios 401 — it either replays the request or
 * rejects with the *refresh* failure, which carries no `response`. A caller that
 * classifies failures by HTTP status therefore cannot tell an expired session
 * from a dropped socket, and a retrying caller (see
 * `seams/decodeProvider.pollExtractionResult`) keeps re-asking an endpoint whose
 * answer will not change. This error carries the terminal status forward so the
 * classification is available where the decision is made.
 */
export class AuthSessionExpiredError extends Error {
  /** Always 401: the status of the response that started the refresh attempt. */
  readonly httpStatus = 401;
  /** The refresh failure (or replay outcome) that ended the session, verbatim. */
  readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "AuthSessionExpiredError";
    this.cause = cause;
  }
}

/** True for the terminal 401 raised by `handle401` once the session is gone. */
export function isAuthSessionExpiredError(error: unknown): boolean {
  return (
    error instanceof AuthSessionExpiredError ||
    (error as { name?: string } | undefined)?.name === "AuthSessionExpiredError"
  );
}

interface AuthFailContext {
  trigger: string;
  refresh_outcome: string;
  trace_id: string;
  stack?: string;
}

function clearTokensAndRedirect(ctx: AuthFailContext): void {
  // Auth-fail observability contract: tests assert this fires before token cleanup.
  logTransportError("[auth-fail]", ctx);

  if (_authFailShowError) {
    _authFailShowError("Session expired. Please sign in again.");
  }

  localStorage.removeItem("auth_tokens");
  localStorage.removeItem("auth_user");

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("lehti_ai:auth-session-expired"));

    if (_authFailNavigate) {
      _authFailNavigate("/?authExpired=1", { replace: true });
    } else {
      window.location.href = "/?authExpired=1";
    }
  }
}

/**
 * Error classification for simulation retry strategy.
 *
 * - 'transient': 503 or network timeout — safe to retry with backoff
 * - 'server':    5xx (not 503) — unexpected server error, may retry
 * - 'client':    4xx — permanent failure, do not retry
 */
export type SimulationErrorKind = "transient" | "server" | "client";

export interface ClassifiedError extends Error {
  kind: SimulationErrorKind;
  httpStatus?: number;
}

interface ErrorWithResponseStatus {
  response?: {
    status?: unknown;
    data?: unknown;
    headers?: unknown;
  };
  config?: {
    data?: unknown;
    skipErrorLogging?: boolean | ((error: unknown) => boolean);
    method?: string;
    url?: string;
  };
}

export function getResponseStatus(error: unknown): number | undefined {
  const status = (error as ErrorWithResponseStatus | undefined)?.response?.status;
  return typeof status === "number" ? status : undefined;
}

function shouldSkipErrorLogging(error: unknown): boolean {
  const rule = (error as ErrorWithResponseStatus | undefined)?.config?.skipErrorLogging;
  if (typeof rule === "function") {
    try {
      return Boolean(rule(error));
    } catch {
      return false;
    }
  }
  return rule === true;
}

export function isNoResponseNetworkError(error: unknown): boolean {
  return (error as ErrorWithResponseStatus | undefined)?.response == null;
}

/**
 * Returns true for axios CanceledError / ERR_CANCELED (AbortController abort)
 * and for any error carrying the ERR_CANCELED code or a CanceledError /
 * AbortError name. These are not application errors: the caller asked for the
 * work to stop, so they must never be logged or turned into a user-visible
 * failure.
 *
 * `axios.isCancel` covers CanceledError instances from this axios version. The
 * additional checks cover non-axios abort shapes — native fetch AbortError, and
 * abort rejections raised by our own cancellable loops (see
 * `seams/decodeProvider.pollExtractionResult`).
 *
 * Lives here, beside `isNoResponseNetworkError`, because it is a transport-level
 * predicate every caller of these clients needs — not the property of any one
 * endpoint module.
 */
export function isCancelError(error: unknown): boolean {
  if (axios.isCancel(error)) return true;
  const shape = error as { code?: string; name?: string } | undefined;
  return (
    shape?.code === "ERR_CANCELED" ||
    shape?.name === "CanceledError" ||
    shape?.name === "AbortError"
  );
}

function logAxiosResponseError(label: string, error: unknown): void {
  if (shouldSkipErrorLogging(error) || isNoResponseNetworkError(error)) {
    return;
  }
  const failed = error as ErrorWithResponseStatus | undefined;
  logResponseFailure(label, {
    status: failed?.response?.status,
    method: failed?.config?.method,
    url: failed?.config?.url,
    data: failed?.response?.data,
    headers: failed?.response?.headers,
  });
}

/**
 * Classify an axios error into a SimulationErrorKind.
 * Attaches `kind` and `httpStatus` to the error object and returns it.
 */
export function classifyError(error: unknown): ClassifiedError {
  const classified = error as ClassifiedError;
  const status = getResponseStatus(error);
  classified.httpStatus = status;

  if (!status) {
    // Network error or timeout — treat as transient
    classified.kind = "transient";
  } else if (status === 503) {
    classified.kind = "transient";
  } else if (status >= 500) {
    classified.kind = "server";
  } else {
    classified.kind = "client";
  }

  return classified;
}

/**
 * Dedicated axios instance for the Django auth service (`VITE_AUTH_BASE_URL`).
 * Uses `attachAuthToken` for JWT injection and `handle401` for automatic token
 * refresh + replay, identical to apiClient / simulationApiClient.
 *
 * Use this for any endpoint on the auth service that requires authentication
 * (property subscriptions, pricing tiers, user settings, etc.).
 */
export const authApiClient = axios.create({
  baseURL: AUTH_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor — attach JWT
authApiClient.interceptors.request.use(
  (config) => {
    attachAuthToken(config);
    stripJsonContentTypeForFormData(config);
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — 401 triggers token refresh + replay
authApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuth401) {
      return handle401(authApiClient, error.config);
    }
    return Promise.reject(error);
  },
);

/**
 * Dedicated axios instance for Django-owned APIs outside `/api/users`.
 * Weather radar playback lives in Django at `/api/v1/weather/...`. Per-stand
 * stored-simulation state is a FastAPI gateway GET at
 * `/api/v1/simulation-runs/...` (use `apiClient`).
 */
export const djangoApiClient = axios.create({
  baseURL: DJANGO_API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

djangoApiClient.interceptors.request.use(
  (config) => {
    attachAuthToken(config);
    stripJsonContentTypeForFormData(config);
    return config;
  },
  (error) => Promise.reject(error),
);

djangoApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuth401) {
      return handle401(djangoApiClient, error.config);
    }
    return Promise.reject(error);
  },
);

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Default 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
});

// Special client for long-running simulation operations
export const simulationApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for long-running simulations
  headers: {
    "Content-Type": "application/json",
  },
});

function stripJsonContentTypeForFormData(
  config: import("axios").InternalAxiosRequestConfig,
): void {
  if (!(config.data instanceof FormData)) {
    return;
  }

  delete config.headers["Content-Type"];
  delete config.headers["content-type"];
}

/**
 * Attach the JWT access token from localStorage to every outgoing request.
 * When no token is present the request proceeds without an Authorization header.
 */
export function getStoredAuthAccessToken(): string | null {
  const raw = localStorage.getItem("auth_tokens");
  if (raw) {
    try {
      const { access } = JSON.parse(raw) as { access?: string };
      if (access) {
        return access;
      }
    } catch {
      // Malformed token data — treat as unauthenticated.
    }
  }
  return null;
}

export function attachAuthToken(
  config: import("axios").InternalAxiosRequestConfig,
): import("axios").InternalAxiosRequestConfig {
  if (
    config.omitAuthorization
    || config.headers.Authorization
    || config.headers.authorization
  ) {
    return config;
  }

  const access = getStoredAuthAccessToken();
  if (access) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
}

// Request interceptor for simulation client
simulationApiClient.interceptors.request.use(
  (config) => {
    attachAuthToken(config);
    stripJsonContentTypeForFormData(config);
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor for simulation client
simulationApiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuth401) {
      return handle401(simulationApiClient, error.config);
    }
    maybeHandleInsufficientCredits(error);
    logAxiosResponseError("Simulation API Error:", error);
    return Promise.reject(error);
  },
);

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    attachAuthToken(config);
    stripJsonContentTypeForFormData(config);
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuth401) {
      return handle401(apiClient, error.config);
    }
    maybeHandleInsufficientCredits(error);
    logAxiosResponseError("API Response Error:", error);
    return Promise.reject(error);
  },
);

export default apiClient;
